import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwBadge } from '../../ui/badge/uw-badge';
import { UwButton } from '../../ui/button/uw-button';
import { CopilotStore } from '../../../core/stores/copilot.store';
import { WorkbenchStore } from '../../../core/stores/workbench.store';
import { DialogService } from '../../../core/services/dialog.service';
import { ToastService } from '../../../core/services/toast.service';
import type { Account } from '../../../core/models/account.model';
import { UwCompactMoneyPipe } from '../../pipes/format.pipes';

@Component({
  selector: 'uw-account-header',
  standalone: true,
  imports: [UwIcon, UwBadge, UwButton, UwCompactMoneyPipe],
  template: `
    @if (account(); as acct) {
      <div class="rounded-xl border border-line bg-surface-elevated p-4 shadow-sm">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <button
              class="mb-2 inline-flex items-center gap-1 text-[11px] font-semibold text-text-muted transition-colors hover:text-ai"
              (click)="back()"
            >
              <uw-icon name="arrow-left" [size]="12" />
              Back to queue
            </button>
            <div class="flex flex-wrap items-center gap-2">
              <h1 class="typo-heading text-primary">{{ acct.name }}</h1>
              <uw-badge [tone]="appetiteTone(acct)" icon="target">{{ acct.appetiteFit }} appetite fit</uw-badge>
              <uw-badge [tone]="riskTone(acct)" icon="gauge">{{ acct.riskLabel }} risk · {{ acct.riskScore }}</uw-badge>
            </div>
            <p class="mt-1 flex items-center gap-1.5 text-[12px] text-text-muted">
              <uw-icon name="map-pin" [size]="12" class="shrink-0" />
              {{ acct.address.street }}, {{ acct.address.city }}, {{ acct.address.state }} {{ acct.address.zip }} · NAICS {{ acct.naics }} — {{ acct.naicsDescription }}
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <uw-button variant="secondary" size="sm" icon="refresh" (click)="runRisk()">Run Risk Analysis</uw-button>
            <uw-button variant="ai" size="sm" icon="calculator" (click)="generateQuote()">Generate Quote</uw-button>
            <uw-button variant="secondary" size="sm" icon="arrow-up" (click)="createReferral()">Create Referral</uw-button>
          </div>
        </div>

        <dl class="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-4">
          <div class="bg-surface px-3 py-2.5">
            <dt class="typo-label">In business</dt>
            <dd class="mt-0.5 text-[13px] font-semibold text-primary">{{ acct.yearsInBusiness }} years</dd>
          </div>
          <div class="bg-surface px-3 py-2.5">
            <dt class="typo-label">Annual revenue</dt>
            <dd class="mt-0.5 typo-mono text-[13px] font-semibold text-primary">{{ acct.annualRevenue | uwCompactMoney }}</dd>
          </div>
          <div class="bg-surface px-3 py-2.5">
            <dt class="typo-label">Employees</dt>
            <dd class="mt-0.5 typo-mono text-[13px] font-semibold text-primary">{{ acct.employees }}</dd>
          </div>
          <div class="bg-surface px-3 py-2.5">
            <dt class="typo-label">Prior carrier</dt>
            <dd class="mt-0.5 text-[13px] font-semibold text-primary">{{ acct.priorCarrier }}</dd>
          </div>
        </dl>
      </div>
    }
  `,
})
export class UwAccountHeader {
  readonly account = input<Account | null>(null);

  private readonly router = inject(Router);
  private readonly copilot = inject(CopilotStore);
  private readonly workbench = inject(WorkbenchStore);
  private readonly dialog = inject(DialogService);
  private readonly toast = inject(ToastService);

  protected back(): void {
    this.copilot.setView('triage');
    this.workbench.setMode('copilot');
    void this.router.navigate(['/workbench']);
  }

  protected appetiteTone(acct: Account): 'success' | 'warning' | 'danger' {
    return acct.appetiteFit === 'Strong' ? 'success' : acct.appetiteFit === 'Moderate' ? 'warning' : 'danger';
  }

  protected riskTone(acct: Account): 'success' | 'warning' | 'danger' {
    return acct.riskScore >= 70 ? 'success' : acct.riskScore >= 55 ? 'warning' : 'danger';
  }

  protected runRisk(): void {
    this.copilot.setActionBanner('Risk Assessment Engine re-running…');
    setTimeout(() => {
      this.copilot.setActionBanner('Risk Analysis complete — score 72 (Moderate)');
      this.toast.success('Risk analysis complete', 'The Risk Assessment Engine finished scoring this account.');
    }, 1600);
  }

  protected generateQuote(): void {
    this.dialog.confirm({
      title: 'Generate quote',
      message: 'Generate the quote from the current technical pricing and send it to the broker?',
      confirmLabel: 'Generate & send',
      onConfirm: () => {
        this.copilot.setActionBanner('Quote generated and sent to broker');
        this.toast.success('Quote generated', 'The quote was sent to the broker for review.');
      },
    });
  }

  protected createReferral(): void {
    this.dialog.confirm({
      title: 'Create referral',
      message: 'Draft the TIV authority referral and route it to David Park (UW Manager)?',
      tone: 'warning',
      confirmLabel: 'Create & route',
      details: ['TIV $18.4M exceeds the $15M Sr. UW authority.', 'The referral memo will be drafted from the current evidence.'],
      onConfirm: () => {
        this.copilot.setActionBanner('Referral routed to David Park');
        this.toast.success('Referral routed', 'Your referral memo was sent to David Park.');
      },
    });
  }
}
